import { useCallback, useRef } from 'react';
import { useToast } from '@examflow/ui';
import {
  earnAchievement,
  getAchievements,
  getStreakData,
  updateStreak,
  getCumulativeStats,
  incrementCumulativeStats,
  type Achievement,
} from '../data/local/achievements';

export interface UseAchievementsOptions {
  onAchievementUnlocked?: (achievement: Achievement) => void;
}

export function useAchievements(options?: UseAchievementsOptions) {
  const toast = useToast();
  const onUnlockedRef = useRef(options?.onAchievementUnlocked);
  onUnlockedRef.current = options?.onAchievementUnlocked;

  const unlockAchievement = useCallback(
    (id: string) => {
      const newlyEarned = earnAchievement(id);
      if (newlyEarned) {
        const achievements = getAchievements();
        const achievement = achievements.find((a) => a.id === id);
        if (achievement) {
          toast.success(`🏆 成就解锁: ${achievement.name}`);
          onUnlockedRef.current?.(achievement);
        }
      }
    },
    [toast]
  );

  /**
   * Call this after a training record is saved.
   * Checks all achievements and unlocks any that are now earned.
   */
  const checkAndUnlockAchievements = useCallback(
    (params: {
      correctCount: number;
      answeredCount: number;
      accuracy: number;
      durationSeconds: number;
      questionCount: number;
    }) => {
      const { correctCount, answeredCount, accuracy, durationSeconds, questionCount } = params;

      // Update cumulative stats first
      const { practiceCount, correctAnswers } = incrementCumulativeStats(1, correctCount);
      const streakData = updateStreak();
      const hour = new Date().getHours();

      // first-step: complete 1 practice session
      unlockAchievement('first-step');

      // streak-3: 3 days in a row
      if (streakData.current >= 3) {
        unlockAchievement('streak-3');
      }

      // streak-7: 7 days in a row
      if (streakData.current >= 7) {
        unlockAchievement('streak-7');
      }

      // century: 100 correct answers total (use cumulative correctAnswers after increment)
      if (correctAnswers >= 100) {
        unlockAchievement('century');
      }

      // perfect: 100% on any quiz
      if (accuracy === 100 && answeredCount >= questionCount && questionCount > 0) {
        unlockAchievement('perfect');
      }

      // night-owl: practice between midnight and 4am
      if (hour >= 0 && hour < 4) {
        unlockAchievement('night-owl');
      }

      // early-bird: practice before 7am
      if (hour >= 5 && hour < 7) {
        unlockAchievement('early-bird');
      }

      // speed-demon: 10 questions in under 60 seconds total
      if (questionCount >= 10 && durationSeconds < 60) {
        unlockAchievement('speed-demon');
      }
    },
    [unlockAchievement]
  );

  return {
    checkAndUnlockAchievements,
    getAchievements,
    getStreakData,
  };
}
